/**
 * The app's one icon primitive (PIVOT-139). Before this, two files in `src/`
 * contained an `<svg>` (`ThemeToggle`'s sun/moon, the Google mark) and
 * everything else was words — on the dashboard that meant four action pills
 * per card distinguished by nothing but reading each label, and `RubricView`
 * was faking a mark system with bare `::before` glyphs (`+`/`×`/`±`/`→`).
 *
 * One file, not a sprite and not one-per-file (frontend/CLAUDE.md §2 bans a
 * barrel; `public/icons.svg` was a sprite that shipped orphaned, PIVOT-137
 * deleted it). `.oxlintrc.json`'s `react/no-multi-comp: {ignoreStateless:
 * true}` comment already sanctions colocating a stateless icon set in one
 * file, the way `RubricView`'s and `ThemeToggle`'s used to live inline.
 *
 * Style, derived rather than borrowed: 20×20 viewBox, ~16px live area,
 * 1.75px stroke, `stroke-linecap: square`/`stroke-linejoin: miter`, no fill.
 * Square terminals because Bricolage 800 and JetBrains Mono are both
 * flat-terminal faces and `.pivot-face` uses `border-radius: 2px` — round
 * caps are the Feather/Lucide default and read as templated (§9 bans that
 * look). A diagonal may overshoot to ~17-18 units for optical weight, same as
 * a serif's overshoot on a round letterform.
 *
 * Colour and size are never set here: `Icon.css` sizes in `em` so a mark
 * tracks whatever type it sits in, and `stroke: currentColor` means the mark
 * always takes the colour its context already declared (a button's label
 * colour, a rule row's `color`) — no hard-coded fill, in either theme.
 */

import type { ReactNode } from 'react'
import './Icon.css'

export type IconName =
  | 'arrow-out'
  | 'check'
  | 'cross'
  | 'plus-minus'
  | 'arrow-turn'
  | 'chevron'
  | 'document-edit'
  | 'mail'
  | 'upload'
  | 'download'
  | 'bars'
  | 'alert'
  | 'sun'
  | 'moon'

/**
 * One `<path>`/group per mark, keyed by name. A `Record` rather than one
 * function per icon: every value here is *used* (read by `Icon` below), so
 * there is nothing for `no-unused-vars` to catch if a mark is added but not
 * yet wired to a call site — the map is the whole inventory of "the
 * thirteen", not just the ones a component currently renders.
 */
const MARKS: Record<IconName, ReactNode> = {
  // Replaces the literal "↗" after "View posting" (MatchCard, MatchListRow) —
  // a shaft plus an open corner, so it reads as "leaves" without a filled head.
  'arrow-out': <path d="M5 15L15 5M9 5H15V11" />,

  // MatchCard/MatchListRow's "Interested" action; RubricView's `+` require
  // marker (a requirement met, not just an addition).
  check: <path d="M4 10L8 14L16 5" />,

  // MatchCard/MatchListRow's "Not interested" action; RubricView's `×` reject
  // marker.
  cross: <path d="M5 5L15 15M15 5L5 15" />,

  // RubricView's `±` "proven but unwanted" marker — the rule genuinely points
  // both ways, drawn as a plus stacked over a minus.
  'plus-minus': <path d="M10 3V9M7 6H13M5 15H15" />,

  // RubricView's `→` lateral-direction marker. Reuses `Landing.css`'s
  // `.landing-turn-arc` geometry (a rising arc into an open corner) at icon
  // scale, so the lateral-match mark IS the brand's turn, not a second shape
  // that happens to also mean "sideways".
  'arrow-turn': <path d="M4 16C4 9 9 4 16 4M11 3L16 4L15 9" />,

  // The one native-chrome artefact left after PIVOT-137: two browser-drawn
  // `<select>` arrows (ProfileReview's intent field, MatchListRow's
  // application-status field). `appearance: none` hides those; this replaces
  // both with one mark.
  chevron: <path d="M5 8L10 13L15 8" />,

  // MatchCard's "Tailor CV" — a page plus a diagonal pencil stroke, so the
  // action reads as "edit this document" rather than a fourth identical pill.
  'document-edit': <path d="M5 4H15V17H5V4Z M9 13L15 7M13.5 5.5L16.5 8.5" />,

  // MatchCard's "Write cover letter" (PIVOT-111) — an envelope, so the
  // action reads as "a letter" beside `document-edit`'s "a document".
  mail: <path d="M3 5H17V15H3V5Z M3 5L10 11L17 5" />,

  // CvUpload's dropzone — the audit's "most template-generic object in the
  // app, and it has no mark at all". An arrow into a tray.
  upload: <path d="M10 3V13M6 7L10 3L14 7M4 16H16" />,

  // DownloadData's export button. Mirrors `upload`'s tray with the arrow
  // reversed, so the two read as a pair.
  download: <path d="M10 3V13M6 9L10 13L14 9M4 16H16" />,

  // Reserved for the score axis (ScoreBadges/RubricView rail) — not wired to
  // a call site by this ticket (out of that component's scope), kept here so
  // the thirteen-mark vocabulary is complete for whichever screen-pass ticket
  // adopts it next.
  bars: <path d="M5 15V9M10 15V5M15 15V11" />,

  // MatchListRow's "did you hear back?" outcome nudge; DeleteAccount's
  // no-undo warning. A triangle, a stem, and a dot (the dot is a
  // near-zero-length stroke — `square` linecap renders that as a small
  // square mark rather than nothing, the same trick a round-cap dot uses).
  alert: <path d="M10 3L17 16H3L10 3Z M10 8V12M10 14.4V14.5" />,

  // ThemeToggle, replacing its two locally-defined round-cap glyphs.
  sun: (
    <>
      <circle cx="10" cy="10" r="3.5" />
      <path d="M10 2.5V5M10 15V17.5M17.5 10H15M5 10H2.5M15.1 4.9L13.3 6.7M6.7 13.3L4.9 15.1M15.1 15.1L13.3 13.3M6.7 6.7L4.9 4.9" />
    </>
  ),

  // ThemeToggle. A closed two-arc path (the standard crescent construction),
  // stroked rather than filled so it stays a hairline outline like every
  // other mark instead of a solid disc.
  moon: <path d="M17.3 11.9A7.1 7.1 0 1 1 8.1 2.7A5.8 5.8 0 0 0 17.3 11.9Z" />,
}

interface IconProps {
  name: IconName
  /** Screen-reader name for a mark with no adjacent visible label — renders
   * `role="img"` + `aria-label`. Omit for a mark that sits beside text or
   * inside a labelled control, which is every call site in this app today;
   * those render `aria-hidden="true"` instead (frontend/CLAUDE.md §6: no SVG
   * `<title>`, `react/jsx-no-literals` would flag it as a literal string). */
  label?: string
  className?: string
}

export function Icon({ name, label, className }: IconProps) {
  const cls = className ? `icon ${className}` : 'icon'
  // Two full returns rather than spreading a conditional props object onto
  // the <svg> (`react/jsx-props-no-spreading`, frontend/docs/javascript-
  // style.md §3) — `aria-hidden` and `role`+`aria-label` are mutually
  // exclusive, so there is no partial-overlap case a shared JSX body would
  // actually save lines on.
  if (label) {
    return (
      <svg
        className={cls}
        viewBox="0 0 20 20"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="square"
        strokeLinejoin="miter"
        role="img"
        aria-label={label}
      >
        {MARKS[name]}
      </svg>
    )
  }
  return (
    <svg
      className={cls}
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="square"
      strokeLinejoin="miter"
      aria-hidden="true"
    >
      {MARKS[name]}
    </svg>
  )
}
